import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, firstValueFrom, fromEvent, interval } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { buildPaginationItems, buildPaginationLabel, extractPagination } from '../../../helper/table-utils';
import { Httpservice } from '../../../services/httpservice';
import { AppPreferencesService } from '../../../services/app-preferences';
import { ServerTimeService } from '../../../services/server-time';
import { CustomTooltipComponent } from '../../../helper/custom-helper/custom-tooltip';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { ReportShellComponent } from '../../structure/report-shell/report-shell';
import {
  AD_MONTH_OPTIONS,
  BS_MONTH_OPTIONS,
  buildAccordionLabel,
  buildDayLabel,
  buildMonthLabel,
  buildReportDateValue,
  getCurrentMonthValue,
} from './data-validation-report.data';
import {
  DataValidationBlockDefinition,
  DataValidationBlockVariationDetail,
  DataValidationDateConversionResponse,
  DataValidationDayCell,
  DataValidationDayDefinition,
  DataValidationPagination,
  DataValidationReportResponse,
  DataValidationStatusCounts,
  DataValidationSummaryResponse,
  DataValidationVerificationResponse,
  DataValidationVerificationStats,
  DataValidationSubstationOption,
  DataValidationSubstationRow,
  DataValidationStatus,
  DataValidationScadaGenerator,
  DataValidationScadaDetailRow,
  DataValidationScadaDetailsResponse,
} from './data-validation-report.interfaces';
import { ManageGeneratorAutocompleteComponent, ManageGeneratorAutocompleteCompleteEvent } from '../../master/manage-generator/manage-generator-autocomplete';
import {
  commitReportAutocompleteValue,
  filterReportOptions,
  findReportOptionByLabel,
  handleReportAutocompleteSearch,
} from '../../reports/shared/report-ui.helpers';

const DATA_VALIDATION_DEFAULT_PAGE_SIZE = 15;
const DATA_VALIDATION_NO_RECORDS_LABEL = 'No records';
const DATA_VALIDATION_SHOWING_RESULTS_LABEL = 'Showing';
const DATA_VALIDATION_RESULTS_LABEL = 'generators';
const DATA_VALIDATION_OWNER_ALL_VALUE = 'all';
const DATA_VALIDATION_AVAILABILITY_ALL_VALUE = 'all';
const DATA_VALIDATION_ERROR_ALL_VALUE = 'all';
const DATA_VALIDATION_SYNC_FRESHNESS_MINUTES = 15;
const DATA_VALIDATION_SCADA_PAGE_SIZE = 30;
type DataValidationAvailabilityFilter = DataValidationStatus | typeof DATA_VALIDATION_AVAILABILITY_ALL_VALUE;
type DataValidationErrorFilter = 'LOW' | 'MEDIUM' | 'HIGH' | typeof DATA_VALIDATION_ERROR_ALL_VALUE;

@Component({
  selector: 'app-data-validation-report-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ReportShellComponent,
    ManageGeneratorAutocompleteComponent,
    CustomTooltipComponent,
  ],
  templateUrl: './data-validation-report.html',
  styleUrl: './data-validation-report.scss',
})
export class DataValidationReportPage implements OnInit {
protected readonly summaryStatusOrder: DataValidationStatus[] = ['FA', 'PA', 'NA'];
protected readonly verificationStatsSections = [
  { key: 'actual_capacity' as const, label: 'Expected Power (MW)', suffix: '' },
  { key: 'generating_capacity' as const, label: 'Actual Power (MW)', suffix: '' },
  { key: 'verification' as const, label: 'Variation', suffix: '%' },
];
protected get monthlyStatsTitle(): string {
  return `Monthly Stats (as on ${this.monthLabel || this.getMonthDisplayLabel()})`;
}

protected get monthlyStatsTitlePrefix(): string {
  return 'Monthly Stats (as on ';
}

protected get monthlyStatsDateLabel(): string {
  return this.monthLabel || this.getMonthDisplayLabel();
}

protected get dailyStatsTitle(): string {
  return `Daily Stats (as on ${this.dailyStatsLabel || (this.dailyStatsDate ? this.formatDateLabel(this.dailyStatsDate) : 'DD-MMM-YYYY')})`;
}

protected get dailyStatsTitlePrefix(): string {
  return 'Daily Stats (as on ';
}

protected get dailyStatsDateLabel(): string {
  return this.dailyStatsLabel || (this.dailyStatsDate ? this.formatDateLabel(this.dailyStatsDate) : 'DD-MMM-YYYY');
}

protected get dataVerificationTitle(): string {
  return `Data Verification (as on ${this.dailyStatsDateLabel})`;
}

protected monthOptions = [...AD_MONTH_OPTIONS];
protected yearOptions: number[];
protected readonly monthNameControl: FormControl;
protected readonly yearControl: FormControl;
protected ownerOptions = [{ label: 'All Owners', value: DATA_VALIDATION_OWNER_ALL_VALUE }];
protected readonly availabilityOptions: { label: string; value: DataValidationAvailabilityFilter }[] = [
  { label: 'All', value: DATA_VALIDATION_AVAILABILITY_ALL_VALUE },
  { label: 'FA', value: 'FA' },
  { label: 'PA', value: 'PA' },
  { label: 'NA', value: 'NA' },
];
protected readonly dataErrorOptions: { label: string; value: DataValidationErrorFilter }[] = [
  { label: 'All', value: DATA_VALIDATION_ERROR_ALL_VALUE },
  { label: 'Low', value: 'LOW' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'High', value: 'HIGH' },
];




  protected substationOptions: DataValidationSubstationOption[] = [];
  protected filteredSubstationOptions: DataValidationSubstationOption[] = [];
  protected substationSearchTerm = '';
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filtersForm: UntypedFormGroup;
  protected readonly availabilityControl = new FormControl<DataValidationAvailabilityFilter>(DATA_VALIDATION_AVAILABILITY_ALL_VALUE);
  protected readonly dataErrorControl = new FormControl<DataValidationErrorFilter>(DATA_VALIDATION_ERROR_ALL_VALUE);
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;

  protected monthLabel = '';
  protected days: DataValidationDayDefinition[] = [];
  protected blocks: DataValidationBlockDefinition[] = [];
  protected rows: DataValidationSubstationRow[] = [];
  protected filteredRows: DataValidationSubstationRow[] = [];
  protected monthlyStats: DataValidationStatusCounts = { FA: 0, PA: 0, NA: 0 };
  protected dailyStats: DataValidationStatusCounts = { FA: 0, PA: 0, NA: 0 };
  protected dataVerification: DataValidationVerificationStats = {
    actual_capacity: 0,
    generating_capacity: 0,
    verification: 0,
  };
  protected blockVariationDetailsByCellKey: Record<string, DataValidationBlockVariationDetail[]> = {};
  protected variationThreshold = 0;
  protected lowVariationThreshold = 5;
  protected mediumVariationThreshold = 10;
  protected highVariationThreshold = 10;
  protected currentBlockNo: number | null = null;
  protected dailyStatsDate: string | null = null;
  protected dailyStatsLabel = '';
  protected lastSyncFileDatetime: string | null = null;
  protected lastSyncFileDatetimeDisplay = 'N/A';
  protected dataVerificationDate: string | null = null;
  protected currentDateMode: 'AD' | 'BS' = 'AD';
  protected pagination: DataValidationPagination = {
    page_no: 1,
    page_size: DATA_VALIDATION_DEFAULT_PAGE_SIZE,
    total_records: 0,
    total_pages: 0,
  };

  protected expandedCellKey: string | null = null;
  protected scadaDetailsModalOpen = false;
  protected scadaDetailsDate = '';
  protected scadaDetailsDateLabel = '';
  protected scadaDetailsParameter = 'P';
  protected scadaDetailsDefaultGeneratorId: number | null = null;
  protected scadaDetailsGeneratorSearchTerm = '';
  protected scadaDetailsGenerators: DataValidationScadaGenerator[] = [];
  protected filteredScadaDetailsGenerators: DataValidationScadaGenerator[] = [];
  protected selectedScadaGeneratorIds: number[] = [];
  protected scadaDetailsRows: DataValidationScadaDetailRow[] = [];
  protected scadaDetailsSortOrder: 'asc' | 'desc' = 'asc';
  protected scadaDetailsPagination: DataValidationPagination = { page_no: 0, page_size: DATA_VALIDATION_SCADA_PAGE_SIZE, total_records: 0, total_pages: 0 };
  protected scadaDetailsLoading = false;
  protected scadaDetailsExporting = false;
  protected showAllScadaDetailsGenerators = false;
  protected readonly scadaParameterOptions = [
    { value: 'P', label: 'Power' },
    { value: 'Level', label: 'Water Level' },
    { value: 'Frequency', label: 'Frequency' },
  ];
  @ViewChild('scadaDetailsTableWrap') private readonly scadaDetailsTableWrap?: ElementRef<HTMLElement>;
  private filterReloadTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly preferencesSvc: AppPreferencesService,
    private readonly serverTimeSvc: ServerTimeService,
  ) {
    this.filtersForm = this.fb.group({
      month: [getCurrentMonthValue(), Validators.required],
      owner: [DATA_VALIDATION_OWNER_ALL_VALUE],
      substation_id: [null],
      page_size: [DATA_VALIDATION_DEFAULT_PAGE_SIZE],
    });
    this.pageSizeControl = this.filtersForm.get('page_size') as FormControl;

    const currentMonthValue = getCurrentMonthValue();
    const [initialYear, initialMonth] = currentMonthValue.split('-').map((part) => Number(part));

    const currentYear = new Date().getFullYear();
    this.yearOptions = Array.from({ length: 11 }, (_, index) => currentYear - 5 + index);

    this.monthNameControl = new FormControl(initialMonth);
    this.yearControl = new FormControl(initialYear);
  }

  async ngOnInit(): Promise<void> {
    this.destroyRef.onDestroy(() => {
      if (this.filterReloadTimer !== null) {
        clearTimeout(this.filterReloadTimer);
      }
    });
    this.bindFilterListeners();
    this.listenToPreferenceChanges();
    this.loadSubstationOptions();
    await this.syncMonthYearFilterMode();
    this.loadReport(1);
     this.setupLiveBlockTicker();
  }

  

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected onFilterSelectionChange(): void {
    this.scheduleFilterReload();
  }

  protected async clearFilters(): Promise<void> {
    await this.resetMonthSelectionForCurrentMode();
    this.filtersForm.patchValue({ owner: DATA_VALIDATION_OWNER_ALL_VALUE }, { emitEvent: false });
    this.availabilityControl.setValue(DATA_VALIDATION_AVAILABILITY_ALL_VALUE, { emitEvent: false });
    this.dataErrorControl.setValue(DATA_VALIDATION_ERROR_ALL_VALUE, { emitEvent: false });
    this.expandedCellKey = null;
    this.substationSearchTerm = '';
    this.applySubstationAutocompleteFilters('');
    this.loadReport(1);
  }

  private applyAvailabilityFilter(): void {
    this.filteredRows = [...this.rows];
  }

  protected trackByStation(_: number, item: DataValidationSubstationRow): number {
    return item.station_id;
  }

  protected trackByDay(_: number, item: DataValidationDayDefinition): number {
    return item.day;
  }

  protected trackByBlock(_: number, item: DataValidationBlockDefinition): number {
    return item.block_no;
  }

  protected getDayHeaderClass(day: DataValidationDayDefinition): string {
    return [
      'data-validation-table__day-header',
      day.is_today ? 'data-validation-table__day-header--today' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  protected getCurrentDayOfMonth(): number | null {
    return this.days.find((day) => day.is_today)?.day ?? null;
  }

  protected getMonthDisplayLabel(): string {
    if (this.monthLabel) {
      return this.monthLabel;
    }

    if (this.currentDateMode === 'BS') {
      return this.getMonthYearLabelFromFilterControls();
    }

    return buildMonthLabel(this.getSelectedMonthValue());
  }

  protected getPaginationLabel(): string {
    return buildPaginationLabel(
      this.pagination,
      DATA_VALIDATION_NO_RECORDS_LABEL,
      DATA_VALIDATION_SHOWING_RESULTS_LABEL,
      DATA_VALIDATION_RESULTS_LABEL,
    );
  }

  protected getPaginationItems(): number[] {
    return buildPaginationItems(this.pagination.total_pages, this.pagination.page_no);
  }

  protected getSummaryCounts(): DataValidationStatusCounts {
    return this.monthlyStats;
  }

  protected canShowPagination(): boolean {
    return this.pagination.total_records > 0;
  }

  protected goToPage(pageNo: number): void {
    if (this.isLoading || pageNo === this.pagination.page_no) {
      return;
    }

    this.loadReport(pageNo);
  }

  protected goToPreviousPage(): void {
    if (this.pagination.page_no <= 1) {
      return;
    }

    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    if (this.pagination.page_no >= this.pagination.total_pages || this.pagination.total_pages === 0) {
      return;
    }

    this.goToPage(this.pagination.page_no + 1);
  }

  protected getMatrixColspan(): number {
    return Math.max(this.days.length + 1, 1);
  }

  protected getCellStatusClass(status: DataValidationStatus): string {
    return `data-validation-table__status-cell--${status.toLowerCase()}`;
  }

  protected getStatusButtonClass(status: DataValidationStatus, active: boolean): string {
    return [
      'data-validation-table__status-button',
      this.getCellStatusClass(status),
      active ? 'data-validation-table__status-button--active' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  protected getCellTitle(cell: DataValidationDayCell): string {
    switch (cell.status) {
      case 'FA':
        return `Full Available (${cell.available_blocks}/${cell.expected_blocks || cell.available_blocks})`;
      case 'PA':
        return `Partial Available (${cell.available_blocks}/${cell.expected_blocks || this.blocks.length})`;
      default:
        return 'Not Available';
    }
  }

  protected shouldShowAvailabilityCell(cell: DataValidationDayCell): boolean {
    return Boolean(cell.status);
  }

  protected isCellExpanded(row: DataValidationSubstationRow, cell: DataValidationDayCell): boolean {
    return this.expandedCellKey === this.createCellKey(row.station_id, cell.day);
  }

  protected async toggleCell(row: DataValidationSubstationRow, cell: DataValidationDayCell): Promise<void> {
    const key = this.createCellKey(row.station_id, cell.day);
    if (this.expandedCellKey === key) {
      this.expandedCellKey = null;
      return;
    }

    const convertedDate = await this.resolveDateForCurrentMode(cell.date);
    const selectedDate = convertedDate?.selectedDate ?? cell.date;
    const selectedDateLabel = convertedDate?.selectedDateLabel ?? this.formatDateLabel(cell.date);
    this.loadDailyStats(selectedDate, selectedDateLabel);
    this.loadVerificationStats(selectedDate, row.station_id, key);
    this.expandedCellKey = key;
  }

  protected openScadaDetailsModal(row: DataValidationSubstationRow): void {
    this.scadaDetailsModalOpen = true;
    this.scadaDetailsDate = this.dailyStatsDate || new Date().toISOString().slice(0, 10);
    this.scadaDetailsParameter = 'P';
    this.scadaDetailsDefaultGeneratorId = row.station_id;
    this.selectedScadaGeneratorIds = [row.station_id];
    this.scadaDetailsSortOrder = 'asc';
    this.scadaDetailsGeneratorSearchTerm = '';
    void this.updateScadaDetailsDateLabel();
    this.filterScadaDetailsGenerators('');
    this.loadScadaDetails(1);
  }

  protected closeScadaDetailsModal(): void {
    this.scadaDetailsModalOpen = false;
    this.scadaDetailsRows = [];
    this.selectedScadaGeneratorIds = [];
  }

  protected onScadaDetailsFilterChange(): void {
    void this.updateScadaDetailsDateLabel();
    this.loadScadaDetails(1);
  }

  protected toggleScadaDetailsSort(): void {
    this.scadaDetailsSortOrder = this.scadaDetailsSortOrder === 'asc' ? 'desc' : 'asc';
    this.loadScadaDetails(1);
  }

  protected clearScadaDetailsFilters(): void {
    this.scadaDetailsDate = this.dailyStatsDate || new Date().toISOString().slice(0, 10);
    this.scadaDetailsParameter = 'P';
    this.scadaDetailsGeneratorSearchTerm = '';
    this.selectedScadaGeneratorIds = this.scadaDetailsDefaultGeneratorId === null ? [] : [this.scadaDetailsDefaultGeneratorId];
    this.scadaDetailsSortOrder = 'asc';
    this.showAllScadaDetailsGenerators = false;
    this.filterScadaDetailsGenerators('');
    this.loadScadaDetails(1);
  }

  protected onScadaDetailsGeneratorSearch(event: ManageGeneratorAutocompleteCompleteEvent): void {
    const query = (event.query || '').trim();
    this.scadaDetailsGeneratorSearchTerm = query;
    this.filterScadaDetailsGenerators(query);
    if (event.originalEvent === null) {
      const selected = this.scadaDetailsGenerators.find((item) => item.name.toLowerCase() === query.toLowerCase());
      if (selected) {
        this.toggleScadaDetailsGenerator(selected.id, !this.isScadaDetailsGeneratorSelected(selected.id));
        this.scadaDetailsGeneratorSearchTerm = '';
        this.filterScadaDetailsGenerators('');
      }
    }
  }

  protected toggleScadaDetailsGenerator(generatorId: number, selected: boolean): void {
    const ids = new Set(this.selectedScadaGeneratorIds);
    if (selected) {
      ids.add(generatorId);
    } else {
      ids.delete(generatorId);
    }
    this.selectedScadaGeneratorIds = [...ids];
    if (this.scadaDetailsModalOpen) {
      this.loadScadaDetails(1);
    }
  }

  protected get visibleScadaDetailsGenerators(): DataValidationScadaGenerator[] {
    const selected = this.scadaDetailsGenerators.filter((generator) => this.isScadaDetailsGeneratorSelected(generator.id));
    return this.showAllScadaDetailsGenerators ? selected : selected.slice(0, 2);
  }

  protected get hiddenScadaDetailsGeneratorCount(): number {
    return Math.max(0, this.selectedScadaGeneratorIds.length - 2);
  }

  protected get selectedScadaDetailsGeneratorOptions(): DataValidationScadaGenerator[] {
    return this.scadaDetailsGenerators.filter((generator) => this.isScadaDetailsGeneratorSelected(generator.id));
  }

  protected get isBsDateMode(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  protected isScadaDetailsGeneratorSelected(generatorId: number): boolean {
    return this.selectedScadaGeneratorIds.includes(generatorId);
  }

  protected getScadaDetailsGeneratorName(generatorId: number): string {
    const generator = this.scadaDetailsGenerators.find((item) => item.id === generatorId);
    if (!generator) {
      return '-';
    }

    const capacityMw = this.getScadaDetailsCapacityMw(generator.installed_capacity);
    return capacityMw === null ? generator.name : `${generator.name} (${this.formatNumber(capacityMw)} MW)`;
  }

  protected getScadaDetailsGeneratorLabel(generator: DataValidationScadaGenerator): string {
    return this.getScadaDetailsGeneratorName(generator.id);
  }

  private getScadaDetailsCapacityMw(capacity: number | null | undefined): number | null {
    if (capacity === null || capacity === undefined || !Number.isFinite(capacity)) {
      return null;
    }

    return capacity / 1000;
  }

  protected getScadaDetailsValue(row: DataValidationScadaDetailRow, generatorId: number): number | null {
    return row.values[String(generatorId)]?.data_value ?? null;
  }

  protected isScadaDetailsAnomaly(row: DataValidationScadaDetailRow, generatorId: number): boolean {
    return row.values[String(generatorId)]?.anomaly ?? false;
  }

  protected getScadaDetailsTimestamp(row: DataValidationScadaDetailRow, generatorId: number): string {
    return this.formatScadaTimestamp(row.values[String(generatorId)]?.data_datetime);
  }

  protected onScadaDetailsScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const nearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 80;
    if (nearBottom && !this.scadaDetailsLoading && this.scadaDetailsPagination.page_no < this.scadaDetailsPagination.total_pages) {
      this.loadScadaDetails(this.scadaDetailsPagination.page_no + 1, true);
    }
  }

  protected exportScadaDetails(): void {
    if (this.scadaDetailsExporting || !this.selectedScadaGeneratorIds.length || !this.scadaDetailsDate) {
      return;
    }
    this.scadaDetailsExporting = true;
    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_DATA_VALIDATION_SCADA_DETAILS, ApiMethod.POST, this.buildScadaDetailsPayload())
      .pipe(finalize(() => (this.scadaDetailsExporting = false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => this.downloadScadaDetailsBlob(blob),
      });
  }

  protected getExpandedCellForRow(row: DataValidationSubstationRow): DataValidationDayCell | null {
    if (!this.expandedCellKey) {
      return null;
    }

    const [stationId, day] = this.expandedCellKey.split('-').map((part) => Number(part));
    if (stationId !== row.station_id || !Number.isFinite(day)) {
      return null;
    }

    return row.cells.find((cell) => cell.day === day) ?? null;
  }

  protected getAccordionLabel(cell: DataValidationDayCell): string {
    return buildAccordionLabel(cell.day, this.getMonthDisplayLabel());
  }

  protected getAccordionBadgeClass(status: DataValidationStatus): string {
    return `data-validation-accordion__badge data-validation-accordion__badge--${status.toLowerCase()}`;
  }

  protected getAccordionValue(cell: DataValidationDayCell, blockNo: number): string {
    const blockValue = cell.blocks.find((item) => item.block_no === blockNo)?.value;
    if (blockValue === null || blockValue === undefined) {
      if (this.isFutureBlockForToday(cell, blockNo)) {
        return '-';
      }
      return 'N/A';
    }

    return this.formatNumber(blockValue);
  }

  protected getBlockVariationDetail(
    row: DataValidationSubstationRow,
    cell: DataValidationDayCell,
    blockNo: number,
  ): DataValidationBlockVariationDetail | null {
    const cellKey = this.createCellKey(row.station_id, cell.day);
    return this.blockVariationDetailsByCellKey[cellKey]?.find((detail) => detail.block_no === blockNo) ?? null;
  }

  protected formatDayHeaderLabel(day: DataValidationDayDefinition): string {
    return day.label || String(day.day);
  }

  protected formatText(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    const text = String(value).trim();
    return text || 'N/A';
  }

  protected getEmptyStateColspan(): number {
    return Math.max(this.days.length + 1, 1);
  }

  protected isCurrentBlockHour(blockNo: number): boolean {
    const currentBlockNo = this.getCurrentBlockNo();
    return currentBlockNo !== null && currentBlockNo === blockNo;
  }

  private isFutureBlockForToday(cell: DataValidationDayCell, blockNo: number): boolean {
    const currentBlockNo = this.getCurrentBlockNo();
    return currentBlockNo !== null && cell.date === this.getKathmanduTodayIsoDate() && blockNo > currentBlockNo;
  }

  private getKathmanduTodayIsoDate(): string {
    const today = this.getKathmanduDateParts();
    return `${today.year}-${String(today.month).padStart(2, '0')}-${String(today.day).padStart(2, '0')}`;
  }

  private bindFilterListeners(): void {
    this.monthNameControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncMonthValue());

    this.yearControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncMonthValue());

    this.filtersForm.get('owner')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.substationSearchTerm = '';
        if (this.normalizeNumber(this.filtersForm.get('substation_id')?.value) !== null) {
          this.filtersForm.patchValue({ substation_id: null }, { emitEvent: false });
        }
        this.applySubstationAutocompleteFilters('');
      });

    this.filtersForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.scheduleFilterReload();
    });

    this.availabilityControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.expandedCellKey = null;
        this.scheduleFilterReload();
      });

    this.dataErrorControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.expandedCellKey = null;
        this.scheduleFilterReload();
      });
}

  private scheduleFilterReload(): void {
    if (this.filterReloadTimer !== null) {
      clearTimeout(this.filterReloadTimer);
    }

    this.filterReloadTimer = setTimeout(() => {
      this.filterReloadTimer = null;
      this.expandedCellKey = null;
      this.loadReport(1);
      this.cdr.markForCheck();
    }, 0);
  }

  private syncMonthValue(): void {
    const year = this.yearControl.value;
    const month = String(this.monthNameControl.value).padStart(2, '0');
    this.filtersForm.patchValue({ month: `${year}-${month}` });
  }

  private async resetMonthSelectionForCurrentMode(): Promise<void> {
    const targetMode = this.getDatePreferenceKey();
    const nextMonthValue = this.getCurrentMonthValueForMode(targetMode);
    this.currentDateMode = targetMode;
    this.setMonthYearOptionsForMode(targetMode, nextMonthValue);
    const [year, month] = nextMonthValue.split('-').map((value) => Number(value));
    this.monthNameControl.setValue(month, { emitEvent: false });
    this.yearControl.setValue(year, { emitEvent: false });
    this.filtersForm.patchValue(
      {
        month: nextMonthValue,
        substation_id: null,
        page_size: DATA_VALIDATION_DEFAULT_PAGE_SIZE,
      },
      { emitEvent: false },
    );
  }

  private async syncMonthYearFilterMode(): Promise<void> {
    const targetMode = this.getDatePreferenceKey();
    const selectedMonthValue = this.getSelectedMonthValue();
    const currentMode = this.currentDateMode;
    const currentMonthValueInCurrentMode = this.getCurrentMonthValueForMode(currentMode);

    let nextMonthValue = selectedMonthValue;
    if (currentMode !== targetMode) {
      if (selectedMonthValue === currentMonthValueInCurrentMode) {
        nextMonthValue = this.getCurrentMonthValueForMode(targetMode);
      } else {
        nextMonthValue = await this.convertMonthValueForMode(selectedMonthValue, targetMode, currentMode);
      }
    }

    this.currentDateMode = targetMode;
    this.setMonthYearOptionsForMode(targetMode, nextMonthValue);
    if (nextMonthValue) {
      const [year, month] = nextMonthValue.split('-').map((value) => Number(value));
      this.monthNameControl.setValue(month, { emitEvent: false });
      this.yearControl.setValue(year, { emitEvent: false });
      this.filtersForm.patchValue({ month: nextMonthValue }, { emitEvent: false });
    }
  }

  private setMonthYearOptionsForMode(mode: 'AD' | 'BS', currentMonthValue: string): void {
    if (mode === 'BS') {
      this.monthOptions = [...BS_MONTH_OPTIONS];
      const bsYear = this.getYearFromMonthValue(currentMonthValue);
      this.yearOptions = Array.from({ length: 11 }, (_, index) => bsYear - 5 + index);
      return;
    }

    this.monthOptions = [...AD_MONTH_OPTIONS];
    const adYear = this.getYearFromMonthValue(currentMonthValue) || new Date().getFullYear();
    this.yearOptions = Array.from({ length: 11 }, (_, index) => adYear - 5 + index);
  }

  private getYearFromMonthValue(monthValue: string): number {
    const year = Number(String(monthValue ?? '').split('-')[0]);
    return Number.isFinite(year) ? year : new Date().getFullYear();
  }

  private getCurrentMonthValueForMode(mode: 'AD' | 'BS'): string {
    if (mode === 'BS') {
      const currentBsDateValue = this.serverTimeSvc.getCurrentBsDateValue();
      if (currentBsDateValue && /^\d{4}-\d{2}-\d{2}$/.test(currentBsDateValue)) {
        return currentBsDateValue.slice(0, 7);
      }
    }

    return getCurrentMonthValue();
  }

  private async convertMonthValueForMode(
    monthValue: string,
    targetMode: 'AD' | 'BS',
    currentMode: 'AD' | 'BS',
  ): Promise<string> {
    const normalizedMonthValue = this.normalizeMonthValue(monthValue, currentMode);
    if (!normalizedMonthValue) {
      return getCurrentMonthValue();
    }

    const conversion = await this.convertDataValidationDate(`${normalizedMonthValue}-01`, currentMode);
    if (!conversion) {
      return normalizedMonthValue;
    }

    const converted = targetMode === 'BS' ? conversion.bs_date : conversion.ad_date;
    return this.toMonthValue(converted) || normalizedMonthValue;
  }

  private async convertDateForMode(dateValue: string, targetMode: 'AD' | 'BS'): Promise<string> {
    const conversion = await this.convertDataValidationDate(dateValue, targetMode === 'BS' ? 'AD' : 'BS');
    if (!conversion) {
      return dateValue;
    }

    return targetMode === 'BS' ? conversion.bs_date : conversion.ad_date;
  }

  private async convertDataValidationDate(
    dateValue: string,
    bsAdKey: 'AD' | 'BS',
  ): Promise<DataValidationDateConversionResponse | null> {
    const normalizedDate = this.normalizeDateValue(dateValue);
    if (!normalizedDate) {
      return null;
    }

    try {
      const response = await firstValueFrom(
        this.httpSvc.requestCall(
          AuthEndPoints.CONVERT_DATA_VALIDATION_DATE,
          ApiMethod.POST,
          {
            data_date: normalizedDate,
            bs_ad_key: bsAdKey,
          },
          false,
          { suppressToast: true },
        ),
      );
      const payloadValue = this.extractResponseValue<DataValidationDateConversionResponse>(response);
      if (!payloadValue) {
        return null;
      }

      return payloadValue;
    } catch {
      return null;
    }
  }

  private normalizeDateValue(value: string): string | null {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized) {
      return null;
    }

    return normalized;
  }

  private normalizeMonthValue(monthValue: string, mode: 'AD' | 'BS'): string | null {
    const normalized = this.normalizeDateValue(monthValue);
    if (!normalized) {
      return null;
    }

    const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(normalized);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month)) {
      return null;
    }

    if (mode === 'AD' && match[3]) {
      return `${match[1]}-${match[2]}`;
    }

    return `${match[1]}-${match[2]}`;
  }

  private toMonthValue(dateValue: string): string | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.normalizeDateValue(dateValue) ?? '');
    if (!match) {
      return null;
    }

    return `${match[1]}-${match[2]}`;
  }

  private getMonthYearLabelFromFilterControls(): string {
    const year = this.yearControl.value;
    const month = Number(this.monthNameControl.value);
    const monthLabel = this.monthOptions.find((option) => option.value === month)?.label ?? '';
    if (!monthLabel || !year) {
      return this.monthLabel || '';
    }

    return `${monthLabel} ${year}`;
  }

  private loadReport(pageNo = this.pagination.page_no): void {
    const monthValue = this.getSelectedMonthValue();
    if (!monthValue) {
      return;
    }

    this.isLoading = true;
    this.expandedCellKey = null;
    this.blockVariationDetailsByCellKey = {};
    const payload = {
      data_date: buildReportDateValue(monthValue),
      owner: this.getSelectedOwnerValue(),
      generator_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      page_no: pageNo,
      page_size: this.pageSizeControl.value ?? DATA_VALIDATION_DEFAULT_PAGE_SIZE,
      bs_ad_key: this.getDatePreferenceKey(),
      availability: this.getSelectedAvailabilityValue(),
      data_error: this.getSelectedDataErrorValue(),
    };

    this.httpSvc
      .requestCall(AuthEndPoints.GET_DATA_VALIDATION_REPORT, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          const payloadValue = this.extractResponseValue<DataValidationReportResponse>(response);
          this.monthLabel = payloadValue?.month_label ?? this.formatMonthValueLabel(monthValue);
          this.days = payloadValue?.days ?? [];
          this.blocks = payloadValue?.blocks ?? [];
          this.rows = payloadValue?.rows ?? [];
          this.applyAvailabilityFilter();
          this.monthlyStats = payloadValue?.monthly_stats ?? { FA: 0, PA: 0, NA: 0 };
          this.dailyStats = payloadValue?.daily_stats ?? { FA: 0, PA: 0, NA: 0 };
          this.dataVerification =
            payloadValue?.data_verification ?? {
              actual_capacity: 0,
              generating_capacity: 0,
              verification: 0,
            };
          this.variationThreshold = payloadValue?.variation_threshold ?? 0;
          this.lowVariationThreshold = payloadValue?.low_variation_threshold ?? 5;
          this.mediumVariationThreshold = payloadValue?.medium_variation_threshold ?? 10;
          this.highVariationThreshold = payloadValue?.high_variation_threshold ?? 10;
          this.currentBlockNo = payloadValue?.current_block_no ?? null;
          this.dailyStatsDate = payloadValue?.daily_stats_date ?? null;
          this.dailyStatsLabel = payloadValue?.daily_stats_label ?? '';
          this.lastSyncFileDatetime = payloadValue?.last_sync_file_datetime ?? null;
          void this.updateLastSyncFileDatetimeDisplay();
          this.dataVerificationDate = this.dailyStatsDate;
          this.pagination =
            payloadValue?.pagination ??
            extractPagination<DataValidationPagination>(response) ??
            extractPagination<DataValidationPagination>(payloadValue) ??
            {
              page_no: pageNo,
              page_size: Number(this.pageSizeControl.value ?? DATA_VALIDATION_DEFAULT_PAGE_SIZE),
              total_records: 0,
              total_pages: 0,
            };
        },
        error: (error) => {
          console.error('Failed to load data validation report', error);
          this.monthLabel = this.formatMonthValueLabel(monthValue);
          this.days = this.buildDaysFromMonth(monthValue);
          this.blocks = [];
          this.rows = [];
          this.applyAvailabilityFilter();
          this.monthlyStats = { FA: 0, PA: 0, NA: 0 };
          this.dailyStats = { FA: 0, PA: 0, NA: 0 };
          this.dataVerification = { actual_capacity: 0, generating_capacity: 0, verification: 0 };
          this.variationThreshold = 0;
          this.lowVariationThreshold = 5;
          this.mediumVariationThreshold = 10;
          this.highVariationThreshold = 10;
          this.currentBlockNo = null;
          this.dailyStatsDate = null;
          this.dailyStatsLabel = '';
          this.lastSyncFileDatetime = null;
          this.lastSyncFileDatetimeDisplay = 'N/A';
          this.dataVerificationDate = null;
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.pageSizeControl.value ?? DATA_VALIDATION_DEFAULT_PAGE_SIZE),
            total_records: 0,
            total_pages: 0,
          };
        },
      });
  }

  private loadDailyStats(selectedDate: string, selectedDateLabel?: string): void {
    const monthValue = this.getSelectedMonthValue();
    if (!monthValue || !selectedDate) {
      return;
    }

    const requestedDate = selectedDate;
    this.dailyStatsDate = requestedDate;
    this.dailyStatsLabel = selectedDateLabel || this.formatDateLabel(requestedDate);

    const payload = {
      data_date: buildReportDateValue(monthValue),
      owner: this.getSelectedOwnerValue(),
      generator_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      selected_date: requestedDate,
      bs_ad_key: this.getDatePreferenceKey(),
    };

    this.httpSvc
      .requestCall(AuthEndPoints.GET_DATA_VALIDATION_STATS, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const payloadValue = this.extractResponseValue<DataValidationSummaryResponse>(response);
          if (!payloadValue || this.dailyStatsDate !== requestedDate) {
            return;
          }

          this.monthlyStats = payloadValue.monthly_stats ?? { FA: 0, PA: 0, NA: 0 };
          this.dailyStats = payloadValue.daily_stats ?? { FA: 0, PA: 0, NA: 0 };
          this.dailyStatsDate = payloadValue.daily_stats_date ?? selectedDate;
          this.dailyStatsLabel = payloadValue.daily_stats_label ?? selectedDateLabel ?? this.formatDateLabel(selectedDate);
          this.lastSyncFileDatetime = payloadValue.last_sync_file_datetime ?? null;
          void this.updateLastSyncFileDatetimeDisplay();
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load data validation stats', error);
        },
      });
  }

  private loadVerificationStats(selectedDate: string, generatorId: number, cellKey: string): void {
    const monthValue = this.getSelectedMonthValue();
    if (!monthValue || !selectedDate) {
      return;
    }

    const requestedDate = selectedDate;
    const requestedCellKey = cellKey;
    this.dataVerificationDate = requestedDate;
    const payload = {
      data_date: buildReportDateValue(monthValue),
      owner: this.getSelectedOwnerValue(),
      generator_id: generatorId,
      selected_date: requestedDate,
      bs_ad_key: this.getDatePreferenceKey(),
    };

    this.httpSvc
      .requestCall(AuthEndPoints.GET_DATA_VALIDATION_VERIFICATION, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const payloadValue = this.extractResponseValue<DataValidationVerificationResponse>(response);
          if (
            !payloadValue ||
            this.dataVerificationDate !== requestedDate ||
            payloadValue.selected_date !== requestedDate ||
            payloadValue.station_id !== generatorId
          ) {
            return;
          }

          this.dataVerification = payloadValue.data_verification ?? {
            actual_capacity: 0,
            generating_capacity: 0,
            verification: 0,
          };
          this.blockVariationDetailsByCellKey[requestedCellKey] = payloadValue.block_variations ?? [];
          this.lowVariationThreshold = payloadValue.low_variation_threshold ?? this.lowVariationThreshold;
          this.mediumVariationThreshold = payloadValue.medium_variation_threshold ?? this.mediumVariationThreshold;
          this.highVariationThreshold = payloadValue.high_variation_threshold ?? this.highVariationThreshold;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load data validation verification', error);
          this.dataVerification = { actual_capacity: 0, generating_capacity: 0, verification: 0 };
          this.blockVariationDetailsByCellKey[requestedCellKey] = [];
          this.cdr.markForCheck();
        },
      });
  }

  private formatCurrentDateLabel(referenceDate = new Date()): string {
    const month = String(referenceDate.getMonth() + 1).padStart(2, '0');
    const year = String(referenceDate.getFullYear()).slice(-2);
    return `${month}-${year}`;
  }


  private buildDaysFromMonth(monthValue: string): DataValidationDayDefinition[] {
    if (!/^\d{4}-\d{2}$/.test(monthValue)) {
      return [];
    }

    const [year, month] = monthValue.split('-').map((value) => Number(value));
    const lastDay = new Date(year, month, 0).getDate();
    const kathmanduToday = this.getKathmanduDateParts();
    const isCurrentMonth = year === kathmanduToday.year && month === kathmanduToday.month;
    const visibleLastDay = isCurrentMonth ? Math.min(lastDay, kathmanduToday.day) : lastDay;

    return Array.from({ length: visibleLastDay }, (_, index) => {
      const day = index + 1;
      return {
        day,
        date: `${monthValue}-${String(day).padStart(2, '0')}`,
        label: String(day),
        is_today: isCurrentMonth && day === kathmanduToday.day,
      };
    });
  }

  private extractResponseValue<T>(response: unknown): T | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const payload = response as { value?: unknown };
    return (payload.value ?? response) as T;
  }

  private getSelectedMonthValue(): string {
    const value = this.filtersForm.get('month')?.value;
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }

    return getCurrentMonthValue();
  }

  private createCellKey(stationId: number, day: number): string {
    return `${stationId}-${day}`;
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 5,
    }).format(value);
  }

  protected formatVerificationValue(value: number | null | undefined, suffix = ''): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return 'N/A';
    }

    const displayValue = suffix === '%' ? Math.abs(value) : value;
    const formatted = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
    }).format(displayValue);

    return `${formatted}${suffix}`;
  }

  protected formatVariationThreshold(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return 'N/A';
    }

    return `${this.formatVerificationValue(Number(value), '%')}`;
  }

  protected getVerificationMetricValue(metricKey: 'actual_capacity' | 'generating_capacity' | 'verification'): number | null | undefined {
    return this.dataVerification[metricKey];
  }

  private formatDateLabel(value: string): string {
    if (this.currentDateMode === 'BS') {
      return this.formatBsDayLabel(value) || value;
    }

    return buildDayLabel(value) || value;
  }

  private formatMonthValueLabel(monthValue: string): string {
    if (this.currentDateMode === 'BS') {
      return this.formatBsMonthLabel(monthValue) || monthValue;
    }

    return buildMonthLabel(monthValue) || monthValue;
  }

  private formatBsMonthLabel(monthValue: string): string {
    const normalized = this.normalizeDateValue(monthValue);
    if (!normalized) {
      return '';
    }

    const match = /^(\d{4})-(\d{2})$/.exec(normalized);
    if (!match) {
      return '';
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const monthLabel = BS_MONTH_OPTIONS.find((option) => option.value === month)?.label ?? '';
    if (!monthLabel || !Number.isFinite(year)) {
      return '';
    }

    return `${monthLabel} ${year}`;
  }

  private formatBsDayLabel(dateValue: string): string {
    const normalized = this.normalizeDateValue(dateValue);
    if (!normalized) {
      return '';
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
    if (!match) {
      return '';
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const monthLabel = BS_MONTH_OPTIONS.find((option) => option.value === month)?.label ?? '';
    if (!monthLabel || !Number.isFinite(year) || !Number.isFinite(day)) {
      return '';
    }

    return `${monthLabel} ${String(day).padStart(2, '0')}, ${year}`;
  }

  private getKathmanduDateParts(referenceDate = new Date()): { year: number; month: number; day: number; hour: number; minute: number } {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kathmandu',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(referenceDate);
    const valueMap = parts.reduce<Record<string, string>>((accumulator, part) => {
      if (part.type !== 'literal') {
        accumulator[part.type] = part.value;
      }
      return accumulator;
    }, {});

    return {
      year: Number(valueMap['year']) || referenceDate.getFullYear(),
      month: Number(valueMap['month']) || referenceDate.getMonth() + 1,
      day: Number(valueMap['day']) || referenceDate.getDate(),
      hour: Number(valueMap['hour']) || referenceDate.getHours(),
      minute: Number(valueMap['minute']) || referenceDate.getMinutes(),
    };
  }

  private async resolveDateForCurrentMode(dateValue: string): Promise<{ selectedDate: string; selectedDateLabel: string } | null> {
    const conversion = await this.convertDataValidationDate(dateValue, this.getDatePreferenceKey());
    if (!conversion) {
      return null;
    }

    return {
      selectedDate: conversion.selected_date,
      selectedDateLabel: conversion.selected_date_label,
    };
  }


protected onSubstationSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
  handleReportAutocompleteSearch({
    query: event?.query,
    form: this.filtersForm,
    controlName: 'substation_id',
    normalizeNumber: (value) => this.normalizeNumber(value),
    filterOptions: (query) => this.filterSubstationAutocomplete(query),
    findOption: (label) => this.findSubstationByLabel(label),
  });
}

protected onSubstationSearchBlur(): void {
  this.commitSubstationAutocompleteValue();
}

  private loadSubstationOptions(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATORS_LIST, ApiMethod.GET, '?page_no=1&page_size=1000', false, { suppressToast: true })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (response) => {
        this.substationOptions = this.extractSubstationOptions(response);
        this.scadaDetailsGenerators = this.substationOptions
          .filter((option) => option.station_code)
          .map((option) => ({
            id: option.id,
            name: option.name,
            element_code: option.station_code || '',
            installed_capacity: option.installed_capacity,
          }));
        this.filterScadaDetailsGenerators(this.scadaDetailsGeneratorSearchTerm);
        this.refreshOwnerOptions();
        this.applySubstationAutocompleteFilters(this.substationSearchTerm);
        this.cdr.markForCheck();
      },
      error: () => {
        this.substationOptions = [];
        this.filteredSubstationOptions = [];
        this.cdr.markForCheck();
      },
    });
}

private filterSubstationAutocomplete(query: string): void {
  this.applySubstationAutocompleteFilters(query);
}

private commitSubstationAutocompleteValue(): void {
  commitReportAutocompleteValue({
    searchTerm: this.substationSearchTerm,
    form: this.filtersForm,
    controlName: 'substation_id',
    normalizeNumber: (value) => this.normalizeNumber(value),
    findOption: (label) => this.findSubstationByLabel(label),
    setSearchTerm: (value) => {
      this.substationSearchTerm = value;
    },
  });
}

private findSubstationByLabel(label: string): DataValidationSubstationOption | null {
  return findReportOptionByLabel(this.filteredSubstationOptions, this.substationOptions, label);
}

private extractSubstationOptions(response: unknown): DataValidationSubstationOption[] {
    const value = this.extractResponseValue<{ generators?: unknown[] }>(response);
    const rows = Array.isArray(value?.generators) ? value.generators : [];
    return rows
      .map((item) => {
      const record = item as Record<string, unknown>;
      return {
        id: Number(record['id']) || 0,
        name: typeof record['generator_name'] === 'string' ? record['generator_name'] : '-',
        station_code: typeof record['scada_station_code'] === 'string' ? record['scada_station_code'] : null,
        owner: typeof record['owner'] === 'string' ? record['owner'] : null,
        installed_capacity: this.normalizeNumber(record['installed_capacity']),
      };
      })
      .filter((option) => this.hasScadaCode(option));
  }

  private filterScadaDetailsGenerators(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    this.filteredScadaDetailsGenerators = this.scadaDetailsGenerators.filter((generator) =>
      !normalizedQuery || generator.name.toLowerCase().includes(normalizedQuery),
    );
  }

  private buildScadaDetailsPayload(): Record<string, unknown> {
    return {
      data_date: this.scadaDetailsDate,
      parameter: this.scadaDetailsParameter,
      generator_ids: this.selectedScadaGeneratorIds,
      page_no: 1,
      page_size: DATA_VALIDATION_SCADA_PAGE_SIZE,
      sort_order: this.scadaDetailsSortOrder,
    };
  }

  private loadScadaDetails(pageNo: number, append = false): void {
    if (!this.scadaDetailsDate || !this.selectedScadaGeneratorIds.length) {
      this.scadaDetailsRows = [];
      this.scadaDetailsPagination = { page_no: 0, page_size: DATA_VALIDATION_SCADA_PAGE_SIZE, total_records: 0, total_pages: 0 };
      return;
    }

    this.scadaDetailsLoading = true;
    const payload = { ...this.buildScadaDetailsPayload(), page_no: pageNo };
    this.httpSvc
      .requestCall(AuthEndPoints.GET_DATA_VALIDATION_SCADA_DETAILS, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(finalize(() => (this.scadaDetailsLoading = false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const result = this.extractResponseValue<DataValidationScadaDetailsResponse>(response);
          if (!result) {
            return;
          }
          this.scadaDetailsGenerators = result.generators || this.scadaDetailsGenerators;
          this.filterScadaDetailsGenerators(this.scadaDetailsGeneratorSearchTerm);
          this.scadaDetailsRows = append ? [...this.scadaDetailsRows, ...(result.rows || [])] : (result.rows || []);
          this.scadaDetailsPagination = result.pagination;
          this.cdr.markForCheck();
          setTimeout(() => this.loadNextScadaDetailsPageIfNeeded());
        },
        error: (error) => {
          console.error('Failed to load SCADA details', error);
          if (!append) {
            this.scadaDetailsRows = [];
          }
        },
      });
  }

  private loadNextScadaDetailsPageIfNeeded(): void {
    const pagination = this.scadaDetailsPagination;
    const tableWrap = this.scadaDetailsTableWrap?.nativeElement;
    const needsMoreRows = tableWrap && tableWrap.scrollHeight <= tableWrap.clientHeight;
    if (needsMoreRows && !this.scadaDetailsLoading && pagination.page_no < pagination.total_pages) {
      this.loadScadaDetails(pagination.page_no + 1, true);
    }
  }

  protected formatScadaTimestamp(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    const [datePart, timePart = '00:00:00'] = value.replace('T', ' ').split(' ');
    const [year, month, day] = datePart.split('-');
    return `${day}-${month}-${year.slice(-2)} ${timePart.split('.')[0]}`;
  }

  private async updateLastSyncFileDatetimeDisplay(): Promise<void> {
    if (!this.lastSyncFileDatetime) {
      this.lastSyncFileDatetimeDisplay = 'N/A';
      return;
    }

    const [datePart, timePart = '00:00:00'] = this.lastSyncFileDatetime.replace('T', ' ').split(' ');
    let dateLabel = this.formatSyncDateLabel(datePart);
    if (this.isBsDateMode) {
      const conversion = await this.convertDataValidationDate(datePart, 'AD');
      dateLabel = conversion?.bs_date_label || dateLabel;
    }

    this.lastSyncFileDatetimeDisplay = `${dateLabel} ${timePart.split('.')[0].slice(0, 5)}`;
    this.cdr.markForCheck();
  }

  private formatSyncDateLabel(dateValue: string): string {
    const [year, month, day] = dateValue.split('-').map((value) => Number(value));
    if (!year || !month || !day) {
      return dateValue;
    }

    const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(new Date(year, month - 1, day));
    return `${String(day).padStart(2, '0')}-${monthLabel}-${year}`;
  }

  private async updateScadaDetailsDateLabel(): Promise<void> {
    if (!this.scadaDetailsDate) {
      this.scadaDetailsDateLabel = '';
      return;
    }
    if (this.isBsDateMode) {
      const conversion = await this.convertDataValidationDate(this.scadaDetailsDate, 'AD');
      this.scadaDetailsDateLabel = conversion?.bs_date_label || this.scadaDetailsDate;
      return;
    }
    const [year, month, day] = this.scadaDetailsDate.split('-');
    this.scadaDetailsDateLabel = `${day}-${month}-${year}`;
  }

  private downloadScadaDetailsBlob(blob: Blob): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `data_validation_scada_details_${this.scadaDetailsDate}.xlsx`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private applySubstationAutocompleteFilters(query: string): void {
    const selectedOwner = this.getSelectedOwnerValue();
    const ownerFilteredOptions = this.substationOptions.filter((option) => {
      if (!this.hasScadaCode(option)) {
        return false;
      }

      return !selectedOwner || this.matchesOwnerFilter(option.owner, selectedOwner);
    });
    this.filteredSubstationOptions = filterReportOptions(ownerFilteredOptions, query);
  }

  private refreshOwnerOptions(): void {
    const uniqueOwners = new Map<string, string>();
    for (const option of this.substationOptions) {
      const owner = typeof option.owner === 'string' ? option.owner.trim() : '';
      if (!owner) {
        continue;
      }

      const normalizedOwner = owner.toLowerCase();
      if (!uniqueOwners.has(normalizedOwner)) {
        uniqueOwners.set(normalizedOwner, owner);
      }
    }

    this.ownerOptions = [
      { label: 'All Owners', value: DATA_VALIDATION_OWNER_ALL_VALUE },
      ...Array.from(uniqueOwners.values()).map((owner) => ({ label: owner, value: owner })),
    ];
  }

  private hasScadaCode(option: DataValidationSubstationOption): boolean {
    return Boolean((option.station_code ?? '').trim());
  }

  private matchesOwnerFilter(optionOwner: string | null | undefined, selectedOwner: string): boolean {
    const normalizedOptionOwner = typeof optionOwner === 'string' ? optionOwner.trim().toLowerCase() : '';
    const normalizedSelectedOwner = selectedOwner.trim().toLowerCase();
    if (!normalizedOptionOwner || !normalizedSelectedOwner) {
      return false;
    }

    if (normalizedOptionOwner === normalizedSelectedOwner) {
      return true;
    }

    const ownerAliases = new Map<string, string[]>([
      ['nea subsidiary', ['nea sub', 'nea subsidiary']],
      ['nea sub', ['nea sub', 'nea subsidiary']],
    ]);
    const selectedAliases = ownerAliases.get(normalizedSelectedOwner) ?? [normalizedSelectedOwner];
    return selectedAliases.includes(normalizedOptionOwner);
  }

  private getSelectedOwnerValue(): string | null {
    const value = this.filtersForm.get('owner')?.value;
    if (typeof value !== 'string') {
      return null;
    }

    const normalized = value.trim();
    if (!normalized || normalized.toLowerCase() === DATA_VALIDATION_OWNER_ALL_VALUE) {
      return null;
    }

    return normalized;
  }

  private getSelectedAvailabilityValue(): DataValidationStatus | null {
    const selectedAvailability = this.availabilityControl.value ?? DATA_VALIDATION_AVAILABILITY_ALL_VALUE;
    if (selectedAvailability === DATA_VALIDATION_AVAILABILITY_ALL_VALUE) {
      return null;
    }

    return selectedAvailability;
  }

  private getSelectedDataErrorValue(): Exclude<DataValidationErrorFilter, typeof DATA_VALIDATION_ERROR_ALL_VALUE> | null {
    const selectedDataError = this.dataErrorControl.value ?? DATA_VALIDATION_ERROR_ALL_VALUE;
    if (selectedDataError === DATA_VALIDATION_ERROR_ALL_VALUE) {
      return null;
    }

    return selectedDataError;
  }

private normalizeNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

  private setupLiveBlockTicker(): void {
    interval(30000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  private getCurrentBlockNo(): number | null {
    if (this.currentBlockNo !== null) {
      return this.currentBlockNo;
    }

    if (!this.blocks.length) {
      return null;
    }

    const kathmanduNow = this.getKathmanduDateParts();
    const currentMinutes = kathmanduNow.hour * 60 + kathmanduNow.minute;

    const blocksWithMinutes = this.blocks
      .map((block) => {
        const match = /^(\d{1,2}):(\d{2})/.exec(block.label?.trim() ?? '');
        if (!match) {
          return null;
        }

        return {
          block,
          minutes: Number(match[1]) * 60 + Number(match[2]),
        };
      })
      .filter((item): item is { block: DataValidationBlockDefinition; minutes: number } => item !== null)
      .sort((a, b) => a.minutes - b.minutes);

    if (!blocksWithMinutes.length) {
      return this.blocks[0].block_no;
    }

    let currentBlockNo = blocksWithMinutes[0].block.block_no;
    for (const item of blocksWithMinutes) {
      if (item.minutes > currentMinutes) {
        return currentBlockNo;
      }
      currentBlockNo = item.block.block_no;
    }

    return blocksWithMinutes[blocksWithMinutes.length - 1].block.block_no;
  }

  protected getCurrentBlockStatus(row: DataValidationSubstationRow): 'available' | 'unavailable' {
    const lastSyncTime = this.parseLastSyncTime(row.last_sync_on);
    if (!lastSyncTime) {
      return 'unavailable';
    }

    const currentTime = this.serverTimeSvc.getCurrentTime() ?? new Date();
    const ageMinutes = (currentTime.getTime() - lastSyncTime.getTime()) / 60000;
    return ageMinutes <= DATA_VALIDATION_SYNC_FRESHNESS_MINUTES ? 'available' : 'unavailable';
  }

  protected getCurrentBlockBadgeClass(row: DataValidationSubstationRow): string {
    const status = this.getCurrentBlockStatus(row);
    return [
      'data-validation-table__live-badge',
      status === 'available'
        ? 'data-validation-table__live-badge--available'
        : 'data-validation-table__live-badge--unavailable',
    ].join(' ');
  }

  protected getCurrentBlockBadgeTitle(row: DataValidationSubstationRow): string {
    return this.getCurrentBlockStatus(row) === 'available'
      ? 'Sync active within 15 minutes'
      : 'Sync delayed by more than 15 minutes';
  }

  private parseLastSyncTime(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const parsedTime = new Date(value);
    return Number.isNaN(parsedTime.getTime()) ? null : parsedTime;
  }

  protected getDatePreferenceKey(): 'AD' | 'BS' {
    return this.preferencesSvc.showBsDate ? 'BS' : 'AD';
  }

  protected getBlockVariationClass(row: DataValidationSubstationRow, cell: DataValidationDayCell, blockNo: number): string {
    const detail = this.getBlockVariationDetail(row, cell, blockNo);
    return detail?.highlighted ? 'data-validation-detail-table__block-cell--variation' : '';
  }

  protected getBlockVariationClasses(row: DataValidationSubstationRow, cell: DataValidationDayCell, blockNo: number): string[] {
    return [
      this.getBlockVariationClass(row, cell, blockNo),
      this.getBlockDataErrorClass(row, cell, blockNo),
    ].filter(Boolean);
  }

  protected getBlockDataErrorClass(row: DataValidationSubstationRow, cell: DataValidationDayCell, blockNo: number): string {
    const blockValue = cell.blocks.find((block) => block.block_no === blockNo)?.value;
    if (blockValue === null || blockValue === undefined) {
      return '';
    }

    const detail = this.getBlockVariationDetail(row, cell, blockNo);
    const variation = detail?.variation_percent_display;
    if (variation === null || variation === undefined || !Number.isFinite(Number(variation))) {
      return '';
    }

    const variationValue = Number(variation);
    if (variationValue <= this.lowVariationThreshold) {
      return 'data-validation-detail-table__block-cell--error-low';
    }
    if (variationValue <= this.mediumVariationThreshold) {
      return 'data-validation-detail-table__block-cell--error-medium';
    }
    return 'data-validation-detail-table__block-cell--error-high';
  }

  protected isBlockVariationHighlighted(row: DataValidationSubstationRow, cell: DataValidationDayCell, blockNo: number): boolean {
    return this.getBlockVariationClass(row, cell, blockNo) !== '';
  }

  protected getBlockVariationTooltip(row: DataValidationSubstationRow, cell: DataValidationDayCell, blockNo: number): string {
    return this.getBlockVariationDetail(row, cell, blockNo)?.tooltip_text ?? '';
  }

  private listenToPreferenceChanges(): void {
    if (typeof window === 'undefined') {
      return;
    }

    fromEvent<CustomEvent<{ name?: string }>>(window, 'app-preferences-change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.detail?.name !== 'showBsDate') {
          return;
        }

        void this.updateScadaDetailsDateLabel();
        void this.updateLastSyncFileDatetimeDisplay();
        void this.handleDateSystemChange();
      });
  }

  private async handleDateSystemChange(): Promise<void> {
    await this.syncMonthYearFilterMode();
    this.loadReport(this.pagination.page_no);
  }
}
